import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Sparkles, ArrowLeft } from 'lucide-react';
interface Participant {
  name: string;
  assignedTo: string;
  hasRevealed: boolean;
}
interface Game {
  id: string;
  gameName: string;
  rules: {
    allowedGifts: string;
    budget: number;
    currency: string;
  };
  participants: Participant[];
}
export function SecretSantaReveal() {
  const {
    gameId
  } = useParams<{
    gameId: string;
  }>();
  const navigate = useNavigate();
  const [step, setStep] = useState<'bag' | 'prayer' | 'countdown' | 'reveal'>('bag');
  const [countdown, setCountdown] = useState(3);
  const [participant, setParticipant] = useState<Participant | null>(null);
  const [game, setGame] = useState<Game | null>(null);
  useEffect(() => {
    // Load game and user session
    const savedGames = localStorage.getItem('secretSantaGames');
    const sessionUser = sessionStorage.getItem(`secretSanta_user_${gameId}`);
    if (savedGames && sessionUser) {
      const games: Game[] = JSON.parse(savedGames);
      const foundGame = games.find(g => g.id === gameId);
      const user: Participant = JSON.parse(sessionUser);
      if (foundGame) {
        setGame(foundGame);
        setParticipant(user);
        // Start animation sequence
        startAnimationSequence();
        // Mark as revealed in localStorage
        if (!user.hasRevealed) {
          const updatedParticipants = foundGame.participants.map(p => p.name === user.name ? {
            ...p,
            hasRevealed: true
          } : p);
          const updatedGame = {
            ...foundGame,
            participants: updatedParticipants
          };
          const updatedGames = games.map(g => g.id === gameId ? updatedGame : g);
          localStorage.setItem('secretSantaGames', JSON.stringify(updatedGames));
        }
      }
    } else {
      navigate(`/fun/secret-santa/${gameId}`);
    }
  }, [gameId, navigate]);
  const startAnimationSequence = () => {
    // Step 1: Bag (2s) -> Prayer
    setTimeout(() => setStep('prayer'), 2000);
    // Step 2: Prayer (3s) -> Countdown
    setTimeout(() => setStep('countdown'), 5000);
  };
  useEffect(() => {
    if (step === 'countdown') {
      if (countdown > 0) {
        const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
        return () => clearTimeout(timer);
      } else {
        setStep('reveal');
      }
    }
  }, [step, countdown]);
  if (!participant || !game) return null;
  const currencySymbol = game.rules.currency === 'INR' ? '₹' : game.rules.currency === 'USD' ? '$' : game.rules.currency === 'EUR' ? '€' : game.rules.currency === 'GBP' ? '£' : game.rules.currency === 'AUD' ? 'A$' : '';
  return <div className="min-h-screen bg-slate-900 text-white flex flex-col items-center justify-center p-4 relative overflow-hidden" data-id="element-550">
      {/* Background decorations */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-551">
        <div className="absolute top-10 left-10 text-4xl opacity-20 animate-bounce" data-id="element-552">
          ❄️
        </div>
        <div className="absolute top-20 right-20 text-3xl opacity-20 animate-pulse" data-id="element-553">
          ✨
        </div>
        <div className="absolute bottom-10 left-1/4 text-5xl opacity-20 animate-bounce" style={{
        animationDelay: '1s'
      }} data-id="element-554">
          🎄
        </div>
        <div className="absolute bottom-20 right-10 text-4xl opacity-20 animate-pulse" style={{
        animationDelay: '0.5s'
      }} data-id="element-555">
          ⭐
        </div>
      </div>

      <div className="z-10 text-center max-w-2xl w-full" data-id="element-556">
        {step === 'bag' && <div className="animate-bounce" data-id="element-557">
            <div className="text-9xl mb-8" data-id="element-558">🎅</div>
            <h2 className="text-3xl font-bold text-red-400" data-id="element-559">Ho Ho Ho!</h2>
            <p className="text-xl mt-4 text-slate-300" data-id="element-560">Checking the list...</p>
          </div>}

        {step === 'prayer' && <div className="animate-fade-in transition-opacity duration-1000" data-id="element-561">
            <div className="text-8xl mb-8 animate-pulse" data-id="element-562">🙏</div>
            <h2 className="text-3xl font-bold text-yellow-300 mb-6" data-id="element-563">
              Make a Wish...
            </h2>
            <p className="text-2xl leading-relaxed text-slate-200" data-id="element-564">
              Close your eyes and send good vibes to your Secret Santa.
              <br data-id="element-565" />
              May the spirit of giving bring joy to everyone!
            </p>
          </div>}

        {step === 'countdown' && <div className="scale-150 transition-transform duration-500" data-id="element-566">
            <div className="text-9xl font-black text-red-500 animate-ping" data-id="element-567">
              {countdown === 0 ? 'GO!' : countdown}
            </div>
          </div>}

        {step === 'reveal' && <div className="animate-scale-up" data-id="element-568">
            <div className="text-8xl mb-6 animate-bounce" data-id="element-569">🎁</div>
            <h3 className="text-2xl text-slate-300 mb-2" data-id="element-570">
              You are the Secret Santa for...
            </h3>

            <div className="bg-white/10 backdrop-blur-md p-8 rounded-2xl border border-white/20 shadow-2xl my-8 transform hover:scale-105 transition-transform duration-300" data-id="element-571">
              <h1 className="text-5xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-red-400 to-yellow-300 animate-gradient" data-id="element-572">
                {participant.assignedTo}
              </h1>
              <div className="mt-4 flex justify-center gap-2" data-id="element-573">
                <Sparkles className="text-yellow-400 animate-spin-slow" data-id="element-574" />
                <Sparkles className="text-yellow-400 animate-spin-slow" style={{
              animationDelay: '0.5s'
            }} data-id="element-575" />
                <Sparkles className="text-yellow-400 animate-spin-slow" style={{
              animationDelay: '1s'
            }} data-id="element-576" />
              </div>
            </div>

            <div className="bg-slate-800/80 p-6 rounded-xl border border-slate-700 max-w-md mx-auto mt-8" data-id="element-577">
              <h4 className="text-lg font-semibold text-slate-300 mb-4 border-b border-slate-700 pb-2" data-id="element-578">
                Gift Guidelines
              </h4>
              <div className="space-y-3 text-left" data-id="element-579">
                <div className="flex justify-between items-center" data-id="element-580">
                  <span className="text-slate-400" data-id="element-581">Budget:</span>
                  <span className="text-xl font-bold text-green-400" data-id="element-582">
                    {currencySymbol}
                    {game.rules.budget.toLocaleString()}
                  </span>
                </div>
                <div data-id="element-583">
                  <span className="text-slate-400 block mb-1" data-id="element-584">
                    Allowed Gifts:
                  </span>
                  <p className="text-white bg-slate-700/50 p-2 rounded" data-id="element-585">
                    {game.rules.allowedGifts || 'Any gift within budget'}
                  </p>
                </div>
              </div>
            </div>

            <Button onClick={() => navigate(`/fun/secret-santa/${gameId}`)} className="mt-8 bg-white text-slate-900 hover:bg-slate-200" data-id="element-586">
              <ArrowLeft className="w-4 h-4 mr-2" data-id="element-587" />
              Back to Game
            </Button>
          </div>}
      </div>

      <style data-id="element-588">{`
        @keyframes gradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 3s ease infinite;
        }
        .animate-scale-up {
          animation: scaleUp 0.5s ease-out forwards;
        }
        @keyframes scaleUp {
          from { transform: scale(0.5); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .animate-spin-slow {
          animation: spin 3s linear infinite;
        }
      `}</style>
    </div>;
}